import {
  CountrySlug,
  isFunnelPath,
  requiresConsent,
  resolveFunnel,
} from 'src/funnelWeb/countries';
import { createStore, useStore } from 'src/utils/createStore';

// Cookie-consent choice for the web funnel of consent countries. Imported by
// src/index.tsx at boot, so it must stay dependency-light: no tracker or
// analytics import here (those live in consentActions.ts).

export type ConsentStatus = 'granted' | 'denied';
export type ConsentChoice = ConsentStatus | null;

export const CONSENT_STORAGE_KEY = 'baobab_funnel_consent';
export const CONSENT_VERSION = 1;
// The CNIL recommends asking again after about 6 months.
export const CONSENT_MAX_AGE_MS = 182 * 24 * 60 * 60 * 1000;

type StoredConsent = { status: ConsentStatus; version: number; decidedAt: string };

// Fallback when localStorage throws (private mode, blocked site data): the
// choice still holds for this page session.
type ConsentSession = { memoryConsent: StoredConsent | null; bannerReopened: boolean };

const INITIAL_SESSION: ConsentSession = { memoryConsent: null, bannerReopened: false };

const consentSession = createStore<ConsentSession>(INITIAL_SESSION);

const isStoredConsent = (value: unknown): value is StoredConsent => {
  const v = value as StoredConsent | null;
  return (
    !!v &&
    (v.status === 'granted' || v.status === 'denied') &&
    typeof v.version === 'number' &&
    typeof v.decidedAt === 'string'
  );
};

const readStored = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      return isStoredConsent(parsed) ? parsed : null;
    }
  } catch {
    /* unreadable storage or malformed JSON: fall back to memory */
  }
  return consentSession.getState().memoryConsent;
};

export const readConsent = (now: number = Date.now()): ConsentChoice => {
  const stored = readStored();
  if (!stored || stored.version !== CONSENT_VERSION) return null;
  const decidedAt = Date.parse(stored.decidedAt);
  if (Number.isNaN(decidedAt) || now - decidedAt > CONSENT_MAX_AGE_MS) return null;
  return stored.status;
};

export const writeConsent = (status: ConsentStatus, now: number = Date.now()): void => {
  const value: StoredConsent = {
    status,
    version: CONSENT_VERSION,
    decidedAt: new Date(now).toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* kept in memory for this session */
  }
  consentSession.setState({ memoryConsent: value, bannerReopened: false });
};

export const reopenConsentBanner = (): void => {
  consentSession.setState(session => ({ ...session, bannerReopened: true }));
};

export const isConsentBannerReopened = (): boolean => consentSession.getState().bannerReopened;

export const subscribeConsent = consentSession.subscribe;

const readConsentNow = () => readConsent();

const selectBannerReopened = (session: ConsentSession) => session.bannerReopened;

export const useConsent = (): ConsentChoice => useStore(consentSession, readConsentNow);

export const useConsentBannerReopened = (): boolean =>
  useStore(consentSession, selectBannerReopened);

/**
 * Whether the consent layer governs this funnel page: always for a consent
 * country, and on any other funnel country once this browser holds a stored
 * choice. The choice belongs to the visitor, not to the URL: someone who
 * refused on /funnel/fr and later lands on a non-country funnel URL (a bare
 * /funnel link, the legacy /funnel/funnel-return) keeps their refusal.
 * A first-time visitor of a non-consent country has no stored choice, so
 * their funnel behaves exactly as before.
 */
export const consentApplies = (country: CountrySlug): boolean =>
  requiresConsent(country) || readConsent() !== null;

/**
 * Whether this funnel country's pages may start third-party trackers and
 * store device identifiers now: where the consent layer applies, only once
 * the stored choice is `granted`.
 */
export const trackingAllowed = (country: CountrySlug): boolean =>
  !consentApplies(country) || readConsent() === 'granted';

/**
 * PostHog boot mode, decided from the URL before the router mounts.
 * undefined = default config (native app, main web app, first-time visitor of
 * a non-consent funnel country); otherwise the consent state to boot in.
 * The native app boots at `/` and never renders the funnel; `isNative` makes
 * that guarantee explicit rather than a consequence of the boot URL.
 */
export const posthogBootConsent = (
  pathname: string,
  { isNative = false }: { isNative?: boolean } = {},
): 'granted' | 'denied' | 'pending' | undefined => {
  if (isNative || !isFunnelPath(pathname)) return undefined;
  if (!consentApplies(resolveFunnel(pathname).country)) return undefined;
  return readConsent() ?? 'pending';
};

export const resetConsentStoreForTests = (): void => {
  consentSession.reset();
};
